import { Box, TextField, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { styles } from './FolderTitleInput.styles';

const CHARACTER_LIMIT = 100;

export const intl = {
  label: 'Name',
  defaultTitle: 'Untitled Folder',
  uniqueNameValidationError: 'Provide a unique name for your folder'
};

interface FolderTitleInputProps {
  title: string;
  error?: boolean;
  helperText?: React.ReactNode;
  characterLimit?: number;
  withLabel?: boolean;
  disabled?: boolean;
  onChange: (value: string) => void;
  onPressEnter?: () => void;
}

export function FolderTitleInput({
  title,
  error = false,
  helperText,
  characterLimit = CHARACTER_LIMIT,
  withLabel = false,
  disabled = false,
  onChange,
  onPressEnter
}: FolderTitleInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [startEditeditingDate, setStartEditeditingDate] = useState(new Date().getTime());
  const isDirty = title !== intl.defaultTitle || startEditeditingDate !== new Date().getTime();

  useEffect(() => {
    if (inputRef.current && title === intl.defaultTitle) {
      inputRef.current.setSelectionRange(0, title.length);
    }
  }, [inputRef.current]);

  useEffect(() => {
    return () => {
      setStartEditeditingDate(new Date().getTime());
    };
  }, []);

  return (
    <TextField
      sx={{ ...styles.input, ...(withLabel ? styles.inputWithLabel : {}) }}
      label={
        withLabel ? (
          <Box sx={styles.label} component="span">
            <span>
              {intl.label}&thinsp;
              <span aria-hidden="true" style={styles.asterisk}>
                *
              </span>
            </span>
            <Typography sx={styles.characterLimit} variant="bodySmall" component="span">
              {title.length}/{characterLimit}
            </Typography>
          </Box>
        ) : null
      }
      fullWidth={true}
      disabled={disabled}
      value={title}
      onChange={(event) => {
        onChange(event.target.value);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' && typeof onPressEnter === 'function') {
          onPressEnter();
        }
      }}
      inputProps={{
        autoFocus: true,
        className: 'no-format',
        'data-test': 'folderTitleInput',
        required: true,
        maxLength: characterLimit,
        ...(!withLabel && { 'aria-label': intl.label })
      }}
      error={isDirty ? error : false}
      helperText={isDirty ? helperText : undefined}
      inputRef={inputRef}
    />
  );
}
